<template>
	<div class="trend-chart-container">
		<v-chart
			v-if="chartOption"
			ref="chart"
			:option="chartOption"
			:style="{ height: '280px', width: '100%' }"
			autoresize
		/>
		<div v-else class="no-data">No trend data available</div>
	</div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import VChart from 'vue-echarts';

use([CanvasRenderer, LineChart, GridComponent, TooltipComponent, LegendComponent]);

export default {
	name: 'SpendingTrendChart',
	components: { VChart },
	props: {
		trendData: { type: Array, required: true },
		formatter: { type: Object, required: true },
		colourMode: { type: String, default: 'light' }
	},
	computed: {
		filteredData() {
			if (!this.trendData) return [];
			// Filter out year-only entries (e.g. "2025", "2026")
			return this.trendData.filter(d => !/^\d{4}$/.test(d.label));
		},
		chartOption() {
			if (!this.filteredData || this.filteredData.length === 0) return null;

			const textColor = this.colourMode === 'dark' ? '#e0e0e0' : '#333';
			const gridColor = this.colourMode === 'dark' ? '#333' : '#e0e0e0';

			const labels = this.filteredData.map(d => d.label);

			return {
				tooltip: {
					trigger: 'axis',
					formatter: (params) => {
						let result = `<strong>${params[0].axisValue}</strong><br/>`;
						params.forEach(p => {
							result += `${p.marker} ${p.seriesName}: ${this.formatter.format(p.value)}<br/>`;
						});
						return result;
					}
				},
				legend: {
					data: ['Income', 'Committed', 'Everyday', 'Future'],
					bottom: 0,
					textStyle: { color: textColor }
				},
				grid: {
					left: '3%',
					right: '4%',
					bottom: '15%',
					top: '10%',
					containLabel: true
				},
				xAxis: {
					type: 'category',
					boundaryGap: false,
					data: labels,
					axisLine: { lineStyle: { color: gridColor } },
					axisLabel: { color: textColor, fontSize: 10 }
				},
				yAxis: {
					type: 'value',
					axisLine: { lineStyle: { color: gridColor } },
					splitLine: { lineStyle: { color: gridColor } },
					axisLabel: {
						color: textColor,
						formatter: (value) => {
							if (value >= 1000) {
								return this.formatter.format(Math.round(value / 1000), true, false) + 'k';
							}
							return this.formatter.format(value, true, false);
						}
					}
				},
				series: [
					{
						name: 'Income',
						type: 'line',
						smooth: true,
						data: this.filteredData.map(d => d[0]),
						lineStyle: { color: '#B4D8D7', width: 2 },
						itemStyle: { color: '#B4D8D7' },
						areaStyle: {
							color: {
								type: 'linear',
								x: 0, y: 0, x2: 0, y2: 1,
								colorStops: [
									{ offset: 0, color: 'rgba(180, 216, 215, 0.4)' },
									{ offset: 1, color: 'rgba(180, 216, 215, 0.05)' }
								]
							}
						}
					},
					{
						name: 'Committed',
						type: 'line',
						smooth: true,
						data: this.filteredData.map(d => d[3]),
						lineStyle: { color: '#413655', width: 2 },
						itemStyle: { color: '#413655' },
						areaStyle: {
							color: {
								type: 'linear',
								x: 0, y: 0, x2: 0, y2: 1,
								colorStops: [
									{ offset: 0, color: 'rgba(65, 54, 85, 0.4)' },
									{ offset: 1, color: 'rgba(65, 54, 85, 0.05)' }
								]
							}
						}
					},
					{
						name: 'Everyday',
						type: 'line',
						smooth: true,
						data: this.filteredData.map(d => d[2]),
						lineStyle: { color: '#9EA2D0', width: 2 },
						itemStyle: { color: '#9EA2D0' },
						areaStyle: {
							color: {
								type: 'linear',
								x: 0, y: 0, x2: 0, y2: 1,
								colorStops: [
									{ offset: 0, color: 'rgba(158, 162, 208, 0.4)' },
									{ offset: 1, color: 'rgba(158, 162, 208, 0.05)' }
								]
							}
						}
					},
					{
						name: 'Future',
						type: 'line',
						smooth: true,
						data: this.filteredData.map(d => d[1]),
						lineStyle: { color: '#D5C9C8', width: 2 },
						itemStyle: { color: '#D5C9C8' },
						areaStyle: {
							color: {
								type: 'linear',
								x: 0, y: 0, x2: 0, y2: 1,
								colorStops: [
									{ offset: 0, color: 'rgba(213, 201, 200, 0.4)' },
									{ offset: 1, color: 'rgba(213, 201, 200, 0.05)' }
								]
							}
						}
					}
				]
			};
		}
	}
};
</script>

<style scoped>
.trend-chart-container {
	width: 100%;
}

.no-data {
	height: 280px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--light-text-secondary);
}
</style>
